import { ArrowUpRight, FileText, Landmark, Bus, Globe } from "lucide-react";
import { Link } from "react-router-dom";
import { useApi } from "@/hooks/useApi";
import { useSeo } from "@/lib/seo";
import { useSite } from "@/context/SiteContext";
import { Prose } from "@/components/Prose";
import { SmartLink } from "@/components/SmartLink";
import { PageState } from "@/components/PageState";
import { DocumentList } from "@/components/DocumentRow";
import { TerminalCard } from "@/components/TerminalCard";
import { AnnouncementList } from "@/components/AnnouncementList";
import { HeroIntro, SKIP_TARGET } from "@/components/home/HeroIntro";
import { Reveal, TiltCard } from "@/components/home/Motion";
import { useHomeFlags } from "@/lib/motion";

const ICONS = [FileText, Landmark, Bus, Globe];

const BrandBars = () => (
  <div className="brand-bars pointer-events-none relative hidden h-64 items-end justify-end gap-3 lg:flex" aria-hidden="true">
    <span className="h-40 w-10 bg-brand-yellow/90" />
    <span className="h-52 w-10 bg-brand-orange" />
    <span className="h-60 w-10 bg-brand-sky" />
    <span className="h-64 w-14 bg-navy" />
    <span className="h-64 w-24 bg-brand-red" />
  </div>
);

const Hero = ({ hero, site }) => (
  <section className="relative overflow-hidden border-b border-line bg-white" data-testid="home-hero">
    <div className="container-site grid items-center gap-10 py-16 sm:py-24 lg:grid-cols-[1.6fr_1fr]">
      <div className="animate-fade-up">
        <p className="eyebrow mb-5">{site.short_name} · Otogar İşletmeciliği</p>
        <h1 className="max-w-3xl text-4xl font-bold leading-[1.05] text-navy sm:text-5xl lg:text-6xl" data-testid="home-hero-title">{hero.heading}</h1>
        <Prose html={hero.html} className="mt-6 text-lg sm:text-xl" testId="home-hero-intro" />
        <dl className="mt-8 flex flex-wrap gap-x-10 gap-y-4 border-t border-line pt-6">
          {hero.items.map((i) => (
            <div key={i.label}>
              <dt className="text-xs font-semibold uppercase tracking-[0.14em] text-ink-muted">{i.label}</dt>
              <dd className="font-display text-xl font-semibold text-ink">{i.title}</dd>
            </div>
          ))}
        </dl>
      </div>
      <BrandBars />
    </div>
  </section>
);

const QuickLinks = ({ section, site }) => (
  <section className="container-site -mt-px py-14" aria-labelledby="hizli-erisim" data-testid="home-quick-links">
    <h2 id="hizli-erisim" className="sr-only">{section.heading}</h2>
    <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {section.items.map((item, idx) => {
        const Icon = ICONS[idx % ICONS.length];
        return (
          <li key={item.title} className="animate-fade-up" style={{ animationDelay: `${idx * 60}ms` }}>
            <SmartLink item={item} testId={`hero-quick-link-${idx + 1}`}
              className="group flex h-full flex-col rounded-md border border-line bg-white p-6 transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:border-navy hover:shadow-md">
              <span className="flex items-center justify-between">
                <Icon className="h-7 w-7 text-navy" aria-hidden="true" />
                <ArrowUpRight className="h-5 w-5 text-ink-muted transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-brand-red" aria-hidden="true" />
              </span>
              <span className="mt-5 font-display text-lg font-semibold text-ink">{item.title}</span>
              <span className="mt-1.5 text-[15px] text-ink-muted">{item.text}</span>
              {item.external && item.href === site?.mkk_url && site?.mkk_note && (
                <span className="mt-2 text-sm font-semibold text-ink-soft" data-testid="home-mkk-note">{site.mkk_note}</span>
              )}
            </SmartLink>
          </li>
        );
      })}
    </ul>
  </section>
);

const SectionHead = ({ title, to, label, testId }) => (
  <div className="mb-6 flex items-end justify-between gap-4">
    <h2 className="section-title">{title}</h2>
    {to && <Link to={to} className="shrink-0 text-sm font-semibold text-navy underline decoration-navy/30 hover:decoration-navy" data-testid={testId}>{label}</Link>}
  </div>
);

export default function Home() {
  const site = useSite();
  const page = useApi("/pages/home");
  const anns = useApi("/announcements?home=true");
  const latest = useApi("/documents/latest?limit=5");
  const terminals = useApi("/terminals");
  const flags = useHomeFlags(site);
  useSeo(page.data?.meta_title, page.data?.meta_description);

  if (!page.data) return <div className="container-site"><PageState loading={page.loading} error={page.error} /></div>;
  const s = Object.fromEntries(page.data.sections.map((x) => [x.key, x]));

  return (
    <div data-testid="home-page">
      {flags.hero3d && s.hero && (
        <HeroIntro reduced={flags.reduced} narrow={flags.narrow}
          title={s.hero.heading.replace(/\s*A\.Ş\.?$/, "")} subtitle={(site.footer_note || "").replace(/\.$/, "")} />
      )}
      {s.hero && (flags.hero3d ? (
        <div id={SKIP_TARGET} tabIndex={-1} className="focus:outline-none" data-testid="home-content-start"><Hero hero={s.hero} site={site} /></div>
      ) : <Hero hero={s.hero} site={site} />)}
      {s.quick_links && <Reveal enabled={flags.anim}><QuickLinks section={s.quick_links} site={site} /></Reveal>}
      <div className="container-site grid gap-14 py-10 lg:grid-cols-[1.15fr_1fr]">
        {s.announcements && (
          <Reveal enabled={flags.anim}><section data-testid="home-announcements">
            <SectionHead title={s.announcements.heading} to="/genel-kurul" label="Genel Kurul" testId="home-announcements-more" />
            {anns.data ? <AnnouncementList items={anns.data} /> : <PageState loading={anns.loading} error={anns.error} />}
          </section></Reveal>
        )}
        {s.latest_documents && (
          <Reveal enabled={flags.anim}><section data-testid="home-latest-documents">
            <SectionHead title={s.latest_documents.heading} to="/belgeler" label="Tüm belgeler" testId="home-all-documents-link" />
            {latest.data ? <DocumentList docs={latest.data} showYear /> : <PageState loading={latest.loading} error={latest.error} />}
          </section></Reveal>
        )}
      </div>
      {s.terminals && (
        <Reveal enabled={flags.anim}><section className="container-site py-14" data-testid="home-terminals">
          <SectionHead title={s.terminals.heading} to="/terminaller" label="Tüm terminaller" testId="home-terminals-more" />
          {terminals.data ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {terminals.data.map((t) => flags.anim ? (
                <TiltCard key={t.slug} enabled><TerminalCard t={t} stats /></TiltCard>
              ) : <TerminalCard key={t.slug} t={t} />)}
            </div>
          ) : <PageState loading={terminals.loading} error={terminals.error} />}
        </section></Reveal>
      )}
    </div>
  );
}
